import { appRoles } from 'core/constants/constants';

/**
 * Routes
 */
export interface BaseRoutes {
    root: string;
    error: string;
    devices: string;
    operators: string;
    trainings: string;
    settings: string;
    lots: string;
    lotsInactive: string;
    lotDetail: string;
    deviceDetail: string;
}

const baseRoutes: BaseRoutes = {
    root: '/',
    error: '/error',
    devices: '/devices',
    operators: '/operators',
    trainings: '/trainings',
    settings: '/settings',
    lots: '/lots',
    lotsInactive: '/lots/filters/deactivate',
    lotDetail: '/lots/:lotId',
    deviceDetail: '/devices/:deviceId',
};

export const switchRoutes: BaseRoutes = { ...baseRoutes };
export const linkRoutes: BaseRoutes = { ...baseRoutes };

type SettingModules =
    | 'automationRules'
    | 'activeDirectoryList'
    | 'activeDirectoryDetails'
    | 'customFields'
    | 'trainingSettings'
    | 'lmsIntegration'
    | 'deviceTypes'
    | 'eapSettings'
    | 'eapSettingsImporting'
    | 'eapSettingsForm'
    | 'deviceConfiguration'
    | 'deviceConfigurationDetail'
    | 'deviceConfigurationCustomAlarmForm'
    | 'predefinedComments'
    | 'predefinedCommentsForm'
    | 'materialDefinitions'
    | 'materialDefinitionsForm'
    | 'operatorCustomNotifications'
    | 'operatorCustomNotificationForm'
    | 'userLocationAssignment';

export const settingsModulePaths: Record<SettingModules, string> = {
    automationRules: `${switchRoutes.settings}/automation-rules`,
    activeDirectoryList: `${switchRoutes.settings}/active-directories`,
    activeDirectoryDetails: `${switchRoutes.settings}/active-directories/:activeDirectoryId`,
    customFields: `${switchRoutes.settings}/custom-fields`,
    lmsIntegration: `${switchRoutes.settings}/lms-integration`,
    trainingSettings: `${switchRoutes.settings}/training-settings`,
    deviceTypes: `${switchRoutes.settings}/device-types`,
    eapSettings: `${switchRoutes.settings}/eap-settings`,
    eapSettingsImporting: `${switchRoutes.settings}/eap-settings/importing`,
    eapSettingsForm: `${switchRoutes.settings}/eap-settings/:eapSettingId`,
    deviceConfiguration: `${switchRoutes.settings}/device-configuration/:id`,
    deviceConfigurationDetail: `${switchRoutes.settings}/device-configuration/:id/:deviceConfigurationId`,
    deviceConfigurationCustomAlarmForm: `${switchRoutes.settings}/device-configuration/:id/custom-notifications/:customAlarmId`,
    predefinedComments: `${switchRoutes.settings}/predefined-comments`,
    predefinedCommentsForm: `${switchRoutes.settings}/predefined-comments/:predefinedCommentId`,
    materialDefinitions: `${switchRoutes.settings}/material-definitions`,
    materialDefinitionsForm: `${switchRoutes.settings}/material-definitions/:materialDefinitionId`,
    operatorCustomNotifications: `${switchRoutes.settings}/operator-custom-notifications`,
    operatorCustomNotificationForm: `${switchRoutes.settings}/operator-custom-notifications/:customAlarmDefinitionId`,
    userLocationAssignment: `${switchRoutes.settings}/user-location-assignment`,
};

export type SettingsModulePath = keyof typeof settingsModulePaths;
export type SettingsModulePathValue = (typeof settingsModulePaths)[SettingsModulePath];

type LotsModules = 'lots' | 'lotsFilter' | 'lotsInactive' | 'lotDetail' | 'releasedLotsInactive';

export const lotsModulePaths: Record<LotsModules, string> = {
    lots: switchRoutes.lots,
    lotsFilter: `${switchRoutes.lots}/filters/:filter`,
    lotsInactive: switchRoutes.lotsInactive,
    lotDetail: switchRoutes.lotDetail,
    releasedLotsInactive: `${switchRoutes.lots}/inactive`,
};

export const routeRoleMapping = {
    [switchRoutes.root]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [switchRoutes.operators]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [`${switchRoutes.operators}/filters/:filter`]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [`${switchRoutes.operators}/:operatorId`]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [switchRoutes.trainings]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [`${switchRoutes.trainings}/:trainingProgramId/stages/:trainingStageId`]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [`${switchRoutes.devices}`]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [`${switchRoutes.devices}/filters/:filter`]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [switchRoutes.deviceDetail]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [`${switchRoutes.settings}`]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.automationRules]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.activeDirectoryList]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.activeDirectoryDetails]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.customFields]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.trainingSettings]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.lmsIntegration]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.deviceTypes]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.eapSettings]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.eapSettingsImporting]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.eapSettingsForm]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.predefinedComments]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.predefinedCommentsForm]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.deviceConfiguration]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.deviceConfigurationDetail]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.deviceConfigurationCustomAlarmForm]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.materialDefinitions]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.materialDefinitionsForm]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.operatorCustomNotifications]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.operatorCustomNotificationForm]: [appRoles.POCC_ADMIN],
    [settingsModulePaths.userLocationAssignment]: [appRoles.POCC_ADMIN],
    [lotsModulePaths.lots]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [lotsModulePaths.lotsFilter]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [lotsModulePaths.releasedLotsInactive]: [appRoles.POCC_ADMIN, appRoles.POCC],
    [lotsModulePaths.lotDetail]: [appRoles.POCC_ADMIN, appRoles.POCC],
};
